import { useEffect, useRef, type RefObject } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { LOCATIONS } from '../data/locations';
import { usePinHighlight } from '../state/pinHighlight';
import { GLOBE_RADIUS, PIN_RADIUS, latLngToVec3, pinFacesCamera } from './geo';

export interface FarSideNodes {
  root: HTMLDivElement | null;
  path: SVGPathElement | null;
  label: HTMLSpanElement | null;
}

const ARC_GAP = 9;
const ARC_HALF = 0.1;
const LABEL_GAP = 10;
const INSET = 10;
const EDGE = 6;
const CORNER = 10;
const PIN_HALF_WIDTH = 17;
const PIN_HEIGHT = 44;
const SELECTED_PIN_HEIGHT = 84;

const PIN_LIST = LOCATIONS.map((c) => ({
  id: c.id,
  name: c.name,
  position: latLngToVec3(c.lat, c.lng, PIN_RADIUS),
  direction: latLngToVec3(c.lat, c.lng, 1).normalize(),
}));
const PINS = new Map(PIN_LIST.map((p) => [p.id, p]));

const _cam = new THREE.Vector3();
const _v = new THREE.Vector3();
const _u = new THREE.Vector3();
const _limb = new THREE.Vector3();
const _center = new THREE.Vector3();
const _probe = new THREE.Vector3();

export function FarSideOverlay({ nodes }: { nodes: RefObject<FarSideNodes> }) {
  return (
    <div
      ref={(el) => {
        nodes.current.root = el;
      }}
      aria-hidden="true"
      className="far-mark"
    >
      <svg className="far-mark__arc">
        <path
          ref={(el) => {
            nodes.current.path = el;
          }}
        />
      </svg>
      <span
        ref={(el) => {
          nodes.current.label = el;
        }}
        className="far-mark__label"
      />
    </div>
  );
}

export function FarSideTracker({ nodes, selected }: { nodes: RefObject<FarSideNodes>; selected: string | null }) {
  const invalidate = useThree((s) => s.invalidate);
  const labelSize = useRef({ w: 0, h: 0 });

  useEffect(() => usePinHighlight.subscribe(() => invalidate()), [invalidate]);
  useEffect(() => invalidate(), [selected, invalidate]);

  useFrame(({ camera, size }) => {
    const { root, path, label } = nodes.current;
    if (!root || !path || !label) return;
    const { slug, origin } = usePinHighlight.getState();
    const pin = slug && origin !== 'pin' ? PINS.get(slug) : undefined;
    camera.updateMatrixWorld();
    camera.getWorldPosition(_cam);
    if (!pin || pinFacesCamera(pin.position, _cam)) {
      root.removeAttribute('data-on');
      return;
    }

    const d = _cam.length();
    _v.copy(_cam).divideScalar(d);
    _u.copy(pin.direction).addScaledVector(_v, -pin.direction.dot(_v));
    if (_u.lengthSq() < 1e-8) {
      _u.set(0, 1, 0).applyQuaternion(camera.quaternion);
      _u.addScaledVector(_v, -_u.dot(_v));
    }
    _u.normalize();
    const r = GLOBE_RADIUS;
    _limb.copy(_v).multiplyScalar((r * r) / d).addScaledVector(_u, r * Math.sqrt(1 - (r * r) / (d * d)));

    const toPx = (p: THREE.Vector3): [number, number] => {
      p.project(camera);
      return [((p.x + 1) * size.width) / 2, ((1 - p.y) * size.height) / 2];
    };
    const [cx, cy] = toPx(_center.set(0, 0, 0));
    const [lx, ly] = toPx(_limb);
    const angle = Math.atan2(ly - cy, lx - cx);
    const limbRadius = Math.hypot(lx - cx, ly - cy);
    const radius = limbRadius + ARC_GAP;
    const at = (a: number, rr: number) => [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
    const [x1, y1] = at(angle - ARC_HALF, radius);
    const [x2, y2] = at(angle + ARC_HALF, radius);
    path.setAttribute(
      'd',
      `M${x1.toFixed(1)} ${y1.toFixed(1)}A${radius.toFixed(1)} ${radius.toFixed(1)} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`,
    );

    const text = selected ? 'Far side' : `${pin.name} · far side`;
    if (label.textContent !== text) {
      label.textContent = text;
      labelSize.current = { w: label.offsetWidth, h: label.offsetHeight };
    }
    const { w, h } = labelSize.current;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const side = Math.abs(cos) > 0.35;
    const covers = (l: number, t: number) =>
      PIN_LIST.some((p) => {
        if (!pinFacesCamera(p.position, _cam)) return false;
        const [px, py] = toPx(_probe.copy(p.position));
        const pinTop = py - (p.id === selected ? SELECTED_PIN_HEIGHT : PIN_HEIGHT);
        return l < px + PIN_HALF_WIDTH && l + w > px - PIN_HALF_WIDTH && t < py && t + h > pinTop;
      });

    const inward = (): [number, number] | null => {
      const [ix, iy] = at(angle, limbRadius - INSET);
      const x = side ? (cos > 0 ? ix - w : ix) : ix - w / 2;
      const y = side ? iy - h / 2 : sin > 0 ? iy - h : iy;
      const inPane = x >= EDGE && y >= EDGE && x + w <= size.width - EDGE && y + h <= size.height - EDGE;
      return inPane && !covers(x, y) ? [x, y] : null;
    };

    const outward = (): [number, number] => {
      const [ax, ay] = at(angle, radius + LABEL_GAP);
      const left = side ? (cos > 0 ? ax : ax - w) : ax - w / 2;
      const top = side ? ay - h / 2 : sin > 0 ? ay : ay - h;
      const x = THREE.MathUtils.clamp(left, EDGE, Math.max(EDGE, size.width - w - EDGE));
      const y = THREE.MathUtils.clamp(top, EDGE, Math.max(EDGE, size.height - h - EDGE));
      if (!covers(x, y)) return [x, y];
      return [cos >= 0 ? size.width - w - CORNER : CORNER, sin >= 0 ? size.height - h - CORNER : CORNER];
    };

    const placed = selected ? inward() : outward();
    if (placed) {
      label.style.transform = `translate(${placed[0].toFixed(1)}px, ${placed[1].toFixed(1)}px)`;
      label.removeAttribute('data-off');
    } else {
      label.setAttribute('data-off', '');
    }
    root.setAttribute('data-on', '');
  });

  return null;
}
